import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useBtcPredictions } from '../../hooks/useBtcPredictions';
import { formatCountdown } from './formatCountdown';
import { getCoinIconUrl } from '../../utils/helpers';
import './BtcPredictions.css';

const TIMEFRAMES = ['1h', '4h', 'daily'];
const TF_LABELS = { '1h': '1h', '4h': '4h', 'daily': 'Daily' };
const BTC_ICON = getCoinIconUrl('BTC');

function formatUsd(price) {
  if (price == null) return '--';
  return '$' + price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function PredictionCard({ current, timeframe, setTimeframe, data, btcPrice, t }) {
  const [countdown, setCountdown] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (!current?.endDate) {
      setCountdown(null);
      return;
    }
    setCountdown(formatCountdown(current.endDate));
    const interval = setInterval(() => {
      const value = formatCountdown(current.endDate);
      setCountdown(value);
      if (value === null) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [current?.endDate]);

  const rawDiff = btcPrice != null && current?.priceToBeat != null
    ? btcPrice - current.priceToBeat
    : null;
  const priceDiff = rawDiff != null && Math.abs(rawDiff) < 0.005 ? 0 : rawDiff;
  const diffPositive = priceDiff != null && priceDiff > 0;
  const diffClass = priceDiff == null ? '' : priceDiff === 0 ? '' : diffPositive ? 'positive' : 'negative';

  return (
    <div className="btcp-card">
      <button
        className="btcp-info-btn"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip((v) => !v)}
        aria-label="Info"
      >
        i
        {showTooltip && (
          <div className="btcp-tooltip">
            {t('btcPredictions.tooltip', 'This is not a poll. Real-time data from prediction markets.')}
          </div>
        )}
      </button>

      <div className="btcp-left">
        <div className="btcp-header">
          <img className="btcp-icon" src={BTC_ICON} alt="BTC" />
          <div>
            <div className="btcp-title">{t('btcPredictions.title', 'Bitcoin Up or Down')}</div>
            <div className="btcp-countdown">
              {countdown || t('btcPredictions.ended', 'Ended')}
              {' '}({t(`btcPredictions.timeframes.${timeframe}`, TF_LABELS[timeframe])})
            </div>
          </div>
        </div>

        {current && (
          <div className="btcp-sentiment">
            <div className="btcp-sentiment-header">
              <div className="btcp-sentiment-item btcp-color-up">
                <span className="btcp-percent-val">{current.upPercent?.toFixed(0)}%</span>
                <span className="btcp-percent-label">{t('btcPredictions.up', 'Up')}</span>
              </div>
              <div className="btcp-sentiment-item btcp-color-down">
                <span className="btcp-percent-label">{t('btcPredictions.down', 'Down')}</span>
                <span className="btcp-percent-val">{current.downPercent?.toFixed(0)}%</span>
              </div>
            </div>
            <div className="btcp-progress">
              <div className="btcp-progress-fill btcp-bg-up" style={{ width: `${current.upPercent}%` }} />
              <div className="btcp-progress-fill btcp-bg-down" style={{ width: `${current.downPercent}%` }} />
            </div>
          </div>
        )}
      </div>

      <div className="btcp-right">
        {current ? (
          <div className="btcp-prices">
            <div>
              <div className="btcp-price-label">{t('btcPredictions.priceToBeat', 'Price to Beat')}</div>
              <div className="btcp-price-value">{formatUsd(current.priceToBeat)}</div>
            </div>
            <div>
              <div className="btcp-price-label">{t('btcPredictions.currentPrice', 'Current Price')}</div>
              <div className="btcp-price-value">
                {btcPrice != null ? formatUsd(btcPrice) : '--'}
              </div>
              {priceDiff != null && (
                <div className={`btcp-price-change ${diffClass}`}>
                  {diffPositive ? '+' : ''}{formatUsd(priceDiff).replace('$-', '-$')}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="btcp-loading">
            <div className="loading-spinner"></div>
          </div>
        )}
      </div>

      <div className="btcp-tabs-vertical">
        {TIMEFRAMES.map((tf) => {
          const isDisabled = !data || data[tf] == null;
          const isActive = tf === timeframe;
          return (
            <button
              key={tf}
              className={`btcp-tab ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
              onClick={() => !isDisabled && setTimeframe(tf)}
              disabled={isDisabled}
            >
              {TF_LABELS[tf]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BtcPredictions({ btcPrice = null, predictionsData, predictionsTimeframe, setPredictionsTimeframe, predictionsLoading }) {
  const { t } = useTranslation();
  const hasExternalData = predictionsData !== undefined;
  const ownHook = useBtcPredictions({ enabled: !hasExternalData });
  const data = hasExternalData ? predictionsData : ownHook.data;
  const loading = hasExternalData ? (predictionsLoading ?? false) : ownHook.loading;
  const error = hasExternalData ? null : ownHook.error;
  const timeframe = hasExternalData ? predictionsTimeframe : ownHook.timeframe;
  const setTimeframe = hasExternalData ? setPredictionsTimeframe : ownHook.setTimeframe;

  const hasAnyData = data && TIMEFRAMES.some((tf) => data[tf] != null);
  if (!loading && !hasAnyData) return null;

  if (loading) {
    return (
      <div className="btcp-card">
        <div className="btcp-loading"><div className="loading-spinner"></div></div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="btcp-card"><div className="btcp-error">{error}</div></div>
    );
  }

  const current = data?.[timeframe] ?? null;

  return (
    <PredictionCard
      current={current}
      timeframe={timeframe}
      setTimeframe={setTimeframe}
      data={data}
      btcPrice={btcPrice}
      t={t}
    />
  );
}

export default BtcPredictions;
